import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Colors } from '@altermaind/web/ui';
import { PaginatedTable, usePaginatedTable } from '@/_shared/components/Table';
import { Column } from '@/_shared/components/Table/types';
import { TableHeader } from '@/_shared/components/TableHeader';
import { ValueInTable } from '@/_shared/components/ValueInTable';
import { formatDateFromBEToLocalizied } from '@/_shared/helpers';
import { SkillsType, StatusAssessment } from '@/swagger/constants/reports-enums';
import { getReportsAssessmentsCall } from '../../state/api/getReportsAssessmentsCall';
import { PAGE_SIZE_REPORTS } from '../../state/constants/pagination';
import { AssessmentEmployee } from '../../state/types';

export const TableAssessmentHardSkills: FC = () => {
  const t = useTranslations('reports.assessments.table');

  const {
    data,
    totalItems,
    loading: loadingGetReportsAssessments,
    execute: executeGetReportsAssessments,
    reset: resetGetReportsAssessments,
  } = getReportsAssessmentsCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    pageSize: PAGE_SIZE_REPORTS,
    totalItems: totalItems || 0,
  });

  const [querySearch, setQuerySearch] = useState<string>('');

  const columns: Column[] = useMemo(() => {
    const dotColors: Record<StatusAssessment, keyof Colors> = {
      SENT: 'warning',
      FINISHED: 'success',
    };

    const statusLabel = (assessmentStatus: AssessmentEmployee['assessment']) => {
      if (!assessmentStatus?.status || !assessmentStatus?.timestamp) return '-';
      const formatted = formatDateFromBEToLocalizied(assessmentStatus.timestamp);
      const label = t(`completionStatusOptions.${assessmentStatus.status.toLocaleLowerCase()}`, {
        timestamp: formatted,
      });
      return label;
    };

    const metricMapping: Record<string, string> = {};
    (data || []).forEach(item => {
      (item.results || []).forEach(r => {
        if (r.metric?.name && r.metric?.id) {
          metricMapping[r.metric.id] = r.metric.name;
        }
      });
    });

    const metricColumns: Column[] = Object.entries(metricMapping).map(([metricId, metricName]) => ({
      key: metricId,
      sortable: true,
      title: metricName,
      renderValue: (item: AssessmentEmployee) => {
        const match = item.results?.find(r => r.metric?.id === metricId);
        return <ValueInTable>{match?.score ?? '-'}</ValueInTable>;
      },
    }));

    return [
      {
        key: 'firstName',
        sortable: true,
        title: t('firstName'),
        renderValue: (item: AssessmentEmployee) => (
          <ValueInTable>{item.firstName || '-'}</ValueInTable>
        ),
      },
      {
        key: 'lastName',
        sortable: true,
        title: t('lastName'),
        renderValue: (item: AssessmentEmployee) => (
          <ValueInTable>{item.lastName || '-'}</ValueInTable>
        ),
      },
      {
        key: 'companyRole',
        sortable: true,
        title: t('role'),
        renderValue: (item: AssessmentEmployee) => (
          <ValueInTable>{item.companyRole || '-'}</ValueInTable>
        ),
      },
      {
        key: 'companyDepartment',
        sortable: true,
        title: t('department'),
        renderValue: (item: AssessmentEmployee) => (
          <ValueInTable>{item.companyDepartment || '-'}</ValueInTable>
        ),
      },
      {
        key: 'companyArea',
        sortable: true,
        title: t('team'),
        renderValue: (item: AssessmentEmployee) => (
          <ValueInTable>{item.companyArea || '-'}</ValueInTable>
        ),
      },
      {
        key: 'assessment',
        sortable: true,
        title: t('completionStatus'),
        renderValue: (item: AssessmentEmployee) => (
          <Box display='flex' alignItems='center'>
            <Box
              width='xs'
              height='xs'
              backgroundColor={dotColors[item.assessment?.status || StatusAssessment.SENT]}
              borderRadius='50%'
              mr='xxs'
            />
            <ValueInTable>{statusLabel(item.assessment)}</ValueInTable>
          </Box>
        ),
      },
      ...metricColumns,
    ];
  }, [t, data]);

  const onSearchCallback = useCallback((value: string) => {
    setQuerySearch(value);
  }, []);

  useEffect(() => {
    executeGetReportsAssessments({
      type: SkillsType.HARD_SKILL,
      length: PAGE_SIZE_REPORTS,
      start: 0,
      q: querySearch || '',
    });
    return () => {
      resetGetReportsAssessments();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySearch, executeGetReportsAssessments]);

  return (
    <Card mt='s'>
      <TableHeader onSearchCallback={onSearchCallback} data-testid='reports-assessments-button' />
      <PaginatedTable
        loading={loadingGetReportsAssessments}
        columns={columns}
        pageSize={PAGE_SIZE_REPORTS}
        call={{ ...getReportsAssessmentsCall }}
        paginedProps={paginedProps}
        onRowClick={() => {
          /* empty */
        }}
      />
    </Card>
  );
};
